import { Injectable, computed, inject, signal } from "@angular/core";
import { Subject, Subscription } from "rxjs";
import { TranscriptionHubService, TRANSCRIPTION_COMPLETED_STEP } from "../transcription-hub.service";
import {
    TRANSCRIPTION_MAX_RECORDING_MS,
    TRANSCRIPTION_MAX_RECORDING_WARNING_MS,
} from "../transcription.model";

export interface ProcessingHandoff {
    transcriptionId: number;
    patientId: string;
}

function formatMs(ms: number): string {
    const total = Math.floor(ms / 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    return [h, m, s].map((v) => String(v).padStart(2, "0")).join(":");
}

@Injectable({ providedIn: "root" })
export class RecordingStateService {
    readonly status = signal<"idle" | "recording" | "paused">("idle");
    readonly recordingId = signal<number | null>(null);
    readonly elapsedMs = signal<number>(0);

    readonly isActive = computed(() => this.status() !== "idle");
    readonly isPaused = computed(() => this.status() === "paused");

    readonly remainingMs = computed(() => Math.max(0, TRANSCRIPTION_MAX_RECORDING_MS - this.elapsedMs()));
    readonly remainingMinutes = computed(() => Math.ceil(this.remainingMs() / 60_000));
    readonly limitWarningActive = computed(
        () =>
            this.isActive() &&
            this.remainingMs() > 0 &&
            this.remainingMs() <= TRANSCRIPTION_MAX_RECORDING_WARNING_MS
    );
    readonly limitReached = computed(() => this.isActive() && this.elapsedMs() >= TRANSCRIPTION_MAX_RECORDING_MS);

    readonly processingHandoff = signal<ProcessingHandoff | null>(null);

    /**
     * Background processing of an ended recording (transcription + SOAP). Drives the left-menu
     * alert dot so the user knows work is still running after they navigate away (VS-1469):
     * "processing" = bluish pulsing dot, "done" = solid green dot, "failed" = red alert dot
     * (kept visible so a background failure isn't lost once the processing screen is closed).
     */
    readonly processingStatus = signal<"idle" | "processing" | "done" | "failed">("idle");
    readonly formattedTime = computed(() => formatMs(this.elapsedMs()));
    readonly isMuted = signal(false);

    readonly patientId = signal('');
    readonly patientName = signal('');
    readonly returnUrl = signal<string | null>(null);
    readonly recordingViewVisible = signal(false);
    readonly endAndTranscribePending = signal(false);
    /** When true, the in-attendance hub owns the flow, so the global navigate-to-consultation handler must not push to the patient profile. */
    readonly inAttendance = signal(false);
    readonly navigateToConsultationRequested$ = new Subject<{ patientId: string; patientName: string; returnUrl: string | null }>();

    private readonly _hub = inject(TranscriptionHubService);
    private _processingSub: Subscription | null = null;

    private _intervalId: ReturnType<typeof setInterval> | null = null;
    private _startTimestamp = 0;
    private _pausedDuration = 0;
    private _pauseTimestamp = 0;

    constructor() {
        window.addEventListener('beforeunload', (e) => {
            if (this.isActive()) {
                e.preventDefault();
                e.returnValue = '';
            }
        });
    }

    setPatientContext(patientId: string, patientName: string, returnUrl: string | null = null): void {
        this.patientId.set(patientId);
        this.patientName.set(patientName);
        this.returnUrl.set(returnUrl);
    }

    requestNavigateToConsultation(): void {
        this.navigateToConsultationRequested$.next({
            patientId: this.patientId(),
            patientName: this.patientName(),
            returnUrl: this.returnUrl(),
        });
    }

    formatDurationShort(ms: number): string {
        const total = Math.floor(ms / 1000);
        const m = Math.floor(total / 60);
        const s = total % 60;
        if (m === 0) return `${s} seg`;
        return `${m} min ${s} seg`;
    }

    startRecording(id: number | null, initialOffsetMs = 0): void {
        this.clearProcessing();
        this.processingHandoff.set(null);
        this._clearInterval();
        this._startTimestamp = Date.now() - initialOffsetMs;
        this._pausedDuration = 0;
        this._pauseTimestamp = 0;
        this.elapsedMs.set(initialOffsetMs);
        this.recordingId.set(id);
        this.status.set("recording");
        this._startTick();
    }

    pause(): void {
        if (this.status() !== "recording") return;
        this._clearInterval();
        this._pauseTimestamp = Date.now();
        this.status.set("paused");
    }

    resume(): void {
        if (this.status() !== "paused") return;
        this._pausedDuration += Date.now() - this._pauseTimestamp;
        this.status.set("recording");
        this._startTick();
    }

    toggleMute(): void {
        this.isMuted.update((v) => !v);
    }

    stopRecording(): void {
        this._clearInterval();
        this.status.set("idle");
        this.recordingId.set(null);
        this.elapsedMs.set(0);
        this.isMuted.set(false);
        this.endAndTranscribePending.set(false);
        this.processingHandoff.set(null);
        this.patientId.set('');
        this.patientName.set('');
        this.returnUrl.set(null);
        this._pausedDuration = 0;
        this._startTimestamp = 0;
    }

    /** Called when a recording is ended and sent for transcription. */
    markProcessing(transcriptionId: number): void {
        this._processingSub?.unsubscribe();
        this.processingStatus.set("processing");
        // The hub listens to ABP notifications app-wide, so completion is detected even after
        // the user closes the processing screen / hub dialog and navigates away.
        this._processingSub = this._hub.getUpdates(transcriptionId).subscribe({
            next: (u) => {
                if (u.failed) {
                    this._settleProcessing("failed");
                } else if (u.activeStep >= TRANSCRIPTION_COMPLETED_STEP) {
                    this._settleProcessing("done");
                }
            },
            // Keep the alert visible rather than vanishing silently if the update stream errors.
            error: () => this._settleProcessing("failed"),
        });
    }

    clearProcessing(): void {
        this._processingSub?.unsubscribe();
        this._processingSub = null;
        this.processingStatus.set("idle");
    }

    private _settleProcessing(status: "done" | "failed"): void {
        this._processingSub?.unsubscribe();
        this._processingSub = null;
        this.processingStatus.set(status);
    }

    private _startTick(): void {
        this._intervalId = setInterval(() => {
            this.elapsedMs.set(Date.now() - this._startTimestamp - this._pausedDuration);
        }, 500);
    }

    private _clearInterval(): void {
        if (this._intervalId !== null) {
            clearInterval(this._intervalId);
            this._intervalId = null;
        }
    }
}
